<template>
  <div class="container" ref="containerRef">
    <!-- Header -->
    <header class="section has-text-centered">
      <h1 class="title has-text-white">Sexploration Drinking Game</h1>
      <p class="subtitle has-text-white">Get ready for wild challenges and playful dares!</p>
    </header>

    <!-- Players Section -->
    <section class="section">
      <h2 class="title is-4 has-text-white">Players</h2>
      <ul>
        <li v-for="player in players" :key="player.id" class="box has-background-dark has-text-white">
          {{ player.name }}
        </li>
      </ul>
      <button class="button is-warning is-fullwidth" @click="openPlayerModal">
        + Add Player
      </button>
    </section>

    <!-- Card Board -->
    <section class="section">
      <div class="columns is-multiline">
        <div
          v-for="card in visibleCards"
          :key="card.id"
          :id="'card-' + card.id"
          class="column is-one-quarter-desktop is-one-third-tablet is-half-mobile"
        >
          <div
            class="card custom-card"
            :class="{
              flipped: card.flipped,
              freepass: card.type === 'freepass',
              [card.category.toLowerCase()]: card.type === 'challenge'
            }"
          >
            <div class="card-content">
              <div v-if="!card.flipped" class="card-face card-front">
                <span v-if="card.type === 'freepass'" class="gift-icon">🎁</span>
              </div>
              <div v-else class="card-face card-back">
                <p>{{ card.text }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Draggable Coin -->
    <div class="coin-container">
      <div
        class="coin"
        :class="{ dragging: isDragging, sliding: isSliding }"
        :style="{ left: coinX + 'px', top: coinY + 'px', transition: coinTransition }"
        @pointerdown="startDrag"
      ></div>
    </div>

    <!-- Add Player Modal (Bulma style) -->
    <div class="modal" :class="{ 'is-active': showPlayerModal }">
      <div class="modal-background" @click="closePlayerModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Add Player</p>
          <button class="delete" aria-label="close" @click="closePlayerModal"></button>
        </header>
        <section class="modal-card-body">
          <div class="field">
            <div class="control">
              <input
                class="input"
                type="text"
                v-model="playerNameInput"
                placeholder="Enter player name"
                @keyup.enter="submitPlayer"
              />
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-success" @click="submitPlayer">Add</button>
          <button class="button" @click="closePlayerModal">Cancel</button>
        </footer>
      </div>
    </div>

    <!-- Notification Modal (Bulma style) -->
    <div class="modal" :class="{ 'is-active': showNotificationModal }">
      <div class="modal-background"></div>
      <div class="modal-content">
        <div class="notification is-info">
          {{ notificationMessage }}
        </div>
      </div>
      <button class="modal-close is-large" aria-label="close" @click="showNotificationModal = false"></button>
    </div>

    <!-- Card Modal (Bulma style) -->
    <div class="modal" :class="{ 'is-active': showCardModal }" @click.self="closeCardModal">
      <div class="modal-background"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Challenge Card</p>
          <button class="delete" aria-label="close" @click="closeCardModal"></button>
        </header>
        <section class="modal-card-body">
          <p>{{ selectedCard?.text }}</p>
        </section>
        <footer class="modal-card-foot">
          <button class="button" @click="closeCardModal">Close</button>
        </footer>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: 'DrinkGame'
}
</script>
<script setup>
import { ref, onMounted } from 'vue';

// ---------------------
// PLAYER MANAGEMENT
// ---------------------
const players = ref([]);
let playerId = 1;
const showPlayerModal = ref(false);
const playerNameInput = ref('');
const showNotificationModal = ref(false);
const notificationMessage = ref('');

function openPlayerModal() {
  showPlayerModal.value = true;
  playerNameInput.value = '';
}

function closePlayerModal() {
  showPlayerModal.value = false;
}

function submitPlayer() {
  if (!playerNameInput.value.trim()) return;
  let name = playerNameInput.value.trim();
  if (name.toLowerCase() === 'maureen') {
    name += ' 💔';
  }
  if (players.value.length > 0) {
    notificationMessage.value = `${name} just joined mid-game and drinks a shot!`;
    showNotificationModal.value = true;
    setTimeout(() => (showNotificationModal.value = false), 2000);
  }
  players.value.push({ id: playerId++, name });
  closePlayerModal();
}

// ---------------------
// DECK & BOARD MANAGEMENT
// ---------------------
const maxVisibleCards = 12;
const fullDeck = ref([]); // remaining cards
const visibleCards = ref([]); // cards currently on board

const categories = ['Drinking', 'Revelation', 'Physical', 'Social', 'Creative'];
function randomChallengeText(id) {
  const actions = [
    "take a shot while whispering your secret fantasy",
    "pass the shot with a sultry wink",
    "chug if you've ever had a midnight rendezvous",
    "toast to love and reveal a naughty secret",
    "recite a seductive line in a husky tone"
  ];
  return `Challenge ${id}: ${actions[Math.floor(Math.random() * actions.length)]}.`;
}

function generateDeck() {
  const cards = [];
  // Generate 180 challenge cards
  for (let i = 1; i <= 180; i++) {
    cards.push({
      id: i,
      type: 'challenge',
      text: randomChallengeText(i),
      category: categories[Math.floor(Math.random() * categories.length)],
      flipped: false
    });
  }
  // Generate 20 free pass cards
  for (let i = 181; i <= 200; i++) {
    cards.push({
      id: i,
      type: 'freepass',
      text: `Free Pass Gift: Redeem this for a free pass on your next challenge!`,
      category: 'FreePass',
      flipped: false
    });
  }
  return shuffle(cards);
}

function shuffle(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

function removeUsedCard(cardId) {
  const index = visibleCards.value.findIndex(c => c.id === cardId);
  if (index !== -1) visibleCards.value.splice(index, 1);
  if (fullDeck.value.length > 0) visibleCards.value.push(fullDeck.value.shift());
}

// ---------------------
// COIN & DRAGGING MECHANICS
// ---------------------
const coinX = ref(0);
const coinY = ref(0);
const coinTransition = ref('none');
const isDragging = ref(false);
const isSliding = ref(false);
let dragOffsetX = 0;
const coinDiameter = 50;
const containerRef = ref(null);

function resetCoinPosition() {
  const container = containerRef.value;
  coinX.value = container.clientWidth / 2 - coinDiameter / 2;
  coinY.value = container.clientHeight - coinDiameter - 20;
  coinTransition.value = 'none';
}

function startDrag(e) {
  isDragging.value = true;
  coinTransition.value = 'none';
  dragOffsetX = e.clientX - coinX.value;
  window.addEventListener('pointermove', onDrag);
  window.addEventListener('pointerup', endDrag);
}

function onDrag(e) {
  if (!isDragging.value) return;
  coinX.value = e.clientX - dragOffsetX;
  isSliding.value = true;
}

function endDrag() {
  isDragging.value = false;
  isSliding.value = false;
  window.removeEventListener('pointermove', onDrag);
  window.removeEventListener('pointerup', endDrag);
  throwCoin();
}

const selectedCard = ref(null);
const showCardModal = ref(false);

function throwCoin() {
  const unflipped = visibleCards.value.filter(c => !c.flipped);
  if (unflipped.length === 0) return;
  const targetCard = unflipped[Math.floor(Math.random() * unflipped.length)];
  selectedCard.value = targetCard;
  const cardEl = document.getElementById(`card-${targetCard.id}`);
  if (!cardEl) return;
  const cardRect = cardEl.getBoundingClientRect();
  const containerRect = containerRef.value.getBoundingClientRect();
  coinTransition.value = 'all 1s ease-out';
  coinX.value = cardRect.left - containerRect.left + cardRect.width / 2 - coinDiameter / 2;
  coinY.value = cardRect.top - containerRect.top + cardRect.height / 2 - coinDiameter / 2;
  setTimeout(() => {
    targetCard.flipped = true;
    showCardModal.value = true;
    removeUsedCard(targetCard.id);
  }, 1100);
}

function closeCardModal() {
  showCardModal.value = false;
  resetCoinPosition();
}

// ---------------------
// INITIALIZATION
// ---------------------
onMounted(() => {
  const deck = generateDeck();
  fullDeck.value = deck.slice(maxVisibleCards);
  visibleCards.value = deck.slice(0, maxVisibleCards);
  resetCoinPosition();
});
</script>

<style scoped>
/* Container & Base Styles */
.container {
  max-width: 900px;
  margin: 20px auto;
  padding: 20px;
  position: relative;
  background: linear-gradient(135deg, #ff6ec4, #7873f5);
  border-radius: 15px;
  box-shadow: 0 8px 16px rgba(0,0,0,0.3);
  min-height: 100vh;
}

/* Board and Card Styles */
.custom-card {
  position: relative;
  border-radius: 8px;
  perspective: 600px;
  transition: transform 0.3s;
  cursor: pointer;
}

.custom-card:hover {
  transform: translateY(-3px);
}

.card-content {
  position: relative;
  width: 100%;
  padding-top: 140%; /* maintain aspect ratio */
}

.card-face {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 8px;
  backface-visibility: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  text-align: center;
  font-size: 0.9rem;
}

.card-front {
  background: #333;
  background-image: 
    linear-gradient(45deg, #444 25%, transparent 25%, transparent 75%, #444 75%, #444),
    linear-gradient(45deg, #444 25%, transparent 25%, transparent 75%, #444 75%, #444);
  background-size: 20px 20px;
  background-position: 0 0, 10px 10px;
}

.card-back {
  background: #fff;
  color: #333;
  transform: rotateY(180deg);
  padding: 8px;
  border-radius: 8px;
}

.flipped .card-back {
  transform: rotateY(0deg);
}

/* Category Colors */
.challenge.drinking .card-front { background: #ff5722; }
.challenge.revelation .card-front { background: #9c27b0; }
.challenge.physical .card-front { background: #4caf50; }
.challenge.social .card-front { background: #03a9f4; }
.challenge.creative .card-front { background: #ffc107; }
.freepass .card-front { background: linear-gradient(135deg, #ff9a9e, #fad0c4); }

.gift-icon {
  font-size: 2rem;
}

/* Coin Styles */
.coin-container {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
  z-index: 100;
}

.coin {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #ffd700, #ffa000);
  box-shadow: 0 0 8px rgba(255,215,0,0.8);
  cursor: grab;
  pointer-events: auto;
  transition: transform 0.2s;
}

.coin:active {
  cursor: grabbing;
  transform: scale(1.1);
}

.dragging, .sliding {
  animation: sparkle 0.5s infinite;
}

@keyframes sparkle {
  0% { box-shadow: 0 0 8px rgba(255,215,0,0.8); }
  50% { box-shadow: 0 0 16px rgba(255,215,0,1); }
  100% { box-shadow: 0 0 8px rgba(255,215,0,0.8); }
}

/* Bulma Modal Overrides (if needed) */
/* (Assuming Bulma CSS is loaded globally) */

/* Responsive adjustments */
@media (max-width: 768px) {
  .container {
    padding: 1rem;
    border-radius: 0;
  }
}

@media (max-width: 480px) {
  .coin {
    width: 40px;
    height: 40px;
  }
}
</style>
